import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';
import {
  entitySuggestionKeys,
  processDraftKeys,
  processKeys,
  recordingDashboardKeys,
  recordingKeys,
  systemKeys,
  orgFunctionKeys,
  taskKeys
} from '../query-keys';

export type EntitySuggestionVerb = 'approve' | 'merge' | 'reject' | 'withdraw';

interface UseEntitySuggestionActionArgs {
  suggestionId: string;
  // Withdraw is excluded here: its invalidation is conditional on the
  // response (`useWithdrawEntitySuggestion`), so it builds on the request
  // chokepoint below instead of this hook's unconditional onSuccess.
  verb: Exclude<EntitySuggestionVerb, 'withdraw'>;
  extraInvalidations?: (qc: QueryClient) => void;
}

/**
 * Single chokepoint for the entity-suggestion verb POST — every action
 * (approve/merge/reject/withdraw) builds its request through here so the URL
 * shape can never drift between hooks.
 */
export async function postEntitySuggestionVerb<Req, Res>(
  suggestionId: string,
  verb: EntitySuggestionVerb,
  data?: Req
): Promise<Res> {
  const { data: response } = await apiClient.post<Res>(
    `/api/v2/entity-suggestion/${suggestionId}/${verb}`,
    data
  );
  return response;
}

/**
 * Every read surface that renders a suggestion ref: recordings, drafts, the
 * dashboard, and show-process (which feeds the canvas suggestionsById map and
 * the published sidebar — without it stale placeholders linger for the query
 * staleTime). All three resolution verbs change how those surfaces render, so
 * they share this one list instead of each copying it. `entityLists` is for
 * verbs that create or extend an entity (approve creates, merge extends).
 */
export function invalidateSuggestionResolutionSurfaces(
  qc: QueryClient,
  { entityLists }: { entityLists: boolean }
): void {
  qc.invalidateQueries({ queryKey: recordingDashboardKeys.all });
  qc.invalidateQueries({ queryKey: recordingKeys.all });
  qc.invalidateQueries({ queryKey: processDraftKeys.all });
  qc.invalidateQueries({ queryKey: processKeys.all });
  if (entityLists) {
    qc.invalidateQueries({ queryKey: systemKeys.all });
    qc.invalidateQueries({ queryKey: orgFunctionKeys.all });
  }
}

/**
 * Generic POST-by-verb mutation for entity-suggestion actions. Always
 * invalidates `taskKeys` and `entitySuggestionKeys`; verb-specific
 * invalidations layer via `extraInvalidations`.
 */
export function useEntitySuggestionAction<Req, Res>({
  suggestionId,
  verb,
  extraInvalidations
}: UseEntitySuggestionActionArgs) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Req) => postEntitySuggestionVerb<Req, Res>(suggestionId, verb, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskKeys.all });
      qc.invalidateQueries({ queryKey: entitySuggestionKeys.all });
      extraInvalidations?.(qc);
    }
  });
}
